import { IonInput, IonItem, IonLabel, useIonLoading, useIonToast } from "@ionic/react";
import {useEffect, useMemo, useRef, useState} from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import InputErrorMessage from "../../../components/InputErrorMessage";
import { updateUser } from "../../../store/userUpdate/actions/update-user";
import { changeUserEmail } from "../../../store/userUpdate/actions/change-user-email";
import { selectCanChangeUserEmail } from "../../../store/userAuthentication/selectors/selectCanChangeUserEmail";
import { selectUser } from "../../../store/userUpdate/selectors/selectUser";
import { FormButtonSubmit } from "../../Profile/components/FormButtonSubmit";
import { validate, format } from "rut.js";

export const UpdateUserForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(selectUser);
  const canChangeEmail = useSelector(selectCanChangeUserEmail);
  const [presentLoading, dismissLoading] = useIonLoading();
  const [presentToast] = useIonToast();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: user.name,
      email: user.email,
      dni: user.dni,
    },
  });

  useEffect(() => {
    setValue("name", user.name);
    setValue("email", user.email);
    setValue("dni", user.dni);
  }, [user, setValue]);

  const normalizeEmail = (email?: string) => (email || "").trim().toLowerCase();

  const onSubmit = async (data: any) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    presentLoading({ message: "Actualizando..." });

    let emailChanged = false;
    try {
      // 1. Email: solo si cambió y el usuario tiene permiso. Si falla, no se guarda nada más.
      if (canChangeEmail && normalizeEmail(data.email) !== normalizeEmail(user.email)) {
        const result: any = await dispatch(changeUserEmail({ id: user.id!, email: data.email }));
        const response = result.payload;

        if (changeUserEmail.fulfilled.match(result) && response.noChange) {
          presentToast({ message: response.message, duration: 5000, color: "primary", position: "top" });
        } else if (
          changeUserEmail.fulfilled.match(result) &&
          response.migsUpdated &&
          response.idokUpdated
        ) {
          emailChanged = true;
          presentToast({ message: response.message, duration: 5000, color: "success", position: "top" });
        } else {
          setValue("email", user.email);
          presentToast({ message: response.message, duration: 8000, color: "danger", position: "top" });
          return;
        }
      }

      // 2. Resto de campos (nombre y rut), igual que antes.
      const result: any = await dispatch(updateUser(data));
      if (updateUser.rejected.match(result)) {
        presentToast({
          message: emailChanged
            ? "El correo se actualizó, pero no se pudieron guardar los demás datos."
            : "Ocurrió un problema actualizando el usuario.",
          duration: 8000,
          color: "danger",
          position: "top",
        });
        return;
      }

      // Si cambió el email nos quedamos en la pantalla para que se vea el resultado.
      if (!emailChanged) {
        history.replace(`/app/rrhh/usuarios/${user.id}`);
      }
    } catch (error) {
      console.log(error);
      presentToast({
        message: "Ocurrió un problema actualizando el usuario.",
        duration: 8000,
        color: "danger",
        position: "top",
      });
    } finally {
      dismissLoading();
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const controllers = useMemo(()=>{
    return [({ field: { onChange, value } }:any) => (
      <IonInput
        onIonChange={(e) => onChange(e.detail.value)}
        value={value}
        {...register("name", {
          required: "Campo obligatorio.",
        })}
      />
    ),({ field: { onChange, value } }:any) => (
      <IonInput
        value={value}
        placeholder="Ej. 12.345.678-9"
        type="text"
        {...register("dni", {
          required: "Campo obligatorio.",
          validate: {
            rut: (value) => validate(value || ""),
          },
        })}
        onIonChange={({ detail: { value } }) => onChange(format(value!))}
      />
    ),({ field: { onChange, value } }:any) => (
      <IonInput
        onIonChange={(e) => onChange(e.detail.value)}
        value={value}
        readonly={!canChangeEmail}
        {...register("email", {
          required: "Campo obligatorio.",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Por favor, verifique el email.",
          },
        })}
      />
    )]
  },[canChangeEmail])

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ padding: 18 }} noValidate>
      <IonItem lines="inset">
        <IonLabel position="stacked">Nombre</IonLabel>
        <Controller
          control={control}
          name="name"
          render={controllers[0]}
        />
      </IonItem>
      {errors.name && (
        <InputErrorMessage errorMessage={errors.name?.message || ""} paddingLeft={15} />
      )}

      <IonItem lines="inset">
        <IonLabel position="stacked">Rut</IonLabel>
        <Controller
          name="dni"
          control={control}
          render={controllers[1]}
        />
      </IonItem>
      {errors.dni && (
        <IonItem lines="none">
          <InputErrorMessage errorMessage={errors.dni?.message || "RUT inválido"} />
        </IonItem>
      )}

      <IonItem lines="inset">
        <IonLabel position="stacked">Email</IonLabel>
        <Controller
          control={control}
          name="email"
          render={controllers[2]}
        />
      </IonItem>
      {errors.email && (
        <InputErrorMessage errorMessage={errors.email?.message || ""} paddingLeft={15} />
      )}

      <FormButtonSubmit loading={submitting} />
    </form>
  );
};
